import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { displayTime } from '@/utils/displayTime'

interface IReportMenu {
	selectedText: string
	start?: number
	end?: number
}

interface IUseTranscriptionErrorReport {
	enabled: boolean
	taskId?: string
	username?: string
	userEmail?: string
}

export const useTranscriptionErrorReport = ({
	enabled,
	taskId,
	username,
	userEmail
}: IUseTranscriptionErrorReport) => {
	const transcriptionTextRef = useRef<HTMLDivElement>(null)
	const [reportData, setReportData] = useState<IReportMenu | null>(null)

	const FINGERPRINT_TEXT_LIMIT = 200

	useEffect(() => {
		if (!enabled) return

		const handleTextSelection = (event: MouseEvent | KeyboardEvent) => {
			// Clicking the report button must not clear the selection before onClick.
			if (
				event.target instanceof Element &&
				event.target.closest('[data-transcription-report-button]')
			) {
				return
			}

			const container = transcriptionTextRef.current
			const selection = window.getSelection()

			if (
				!container ||
				!selection ||
				selection.isCollapsed ||
				!selection.rangeCount
			) {
				setReportData(null)
				return
			}

			const range = selection.getRangeAt(0)
			const selectedChunks = Array.from(
				container.querySelectorAll<HTMLElement>(
					'[data-transcription-text][data-start][data-end]'
				)
			).filter(element => range.intersectsNode(element))

			const selectedParts: string[] = []
			const starts: number[] = []
			const ends: number[] = []

			for (const element of selectedChunks) {
				const chunkRange = document.createRange()
				chunkRange.selectNodeContents(element)

				const intersection = range.cloneRange()
				if (
					intersection.compareBoundaryPoints(Range.START_TO_START, chunkRange) <
					0
				) {
					intersection.setStart(
						chunkRange.startContainer,
						chunkRange.startOffset
					)
				}
				if (
					intersection.compareBoundaryPoints(Range.END_TO_END, chunkRange) > 0
				) {
					intersection.setEnd(chunkRange.endContainer, chunkRange.endOffset)
				}

				const text = intersection.toString().trim()
				if (!text) continue

				selectedParts.push(text)
				const start = Number(element.dataset.start)
				const end = Number(element.dataset.end)
				if (Number.isFinite(start)) starts.push(start)
				if (Number.isFinite(end)) ends.push(end)
			}

			if (!selectedParts.length) {
				setReportData(null)
				return
			}

			setReportData({
				selectedText: selectedParts.join('\n'),
				start: starts.length ? Math.min(...starts) : undefined,
				end: ends.length ? Math.max(...ends) : undefined
			})
		}

		document.addEventListener('mouseup', handleTextSelection)
		document.addEventListener('keyup', handleTextSelection)

		return () => {
			document.removeEventListener('mouseup', handleTextSelection)
			document.removeEventListener('keyup', handleTextSelection)
		}
	}, [enabled])

	const getFingerprintText = (text: string) =>
		text.trim().replace(/\s+/g, ' ').slice(0, FINGERPRINT_TEXT_LIMIT)

	const reportTranscriptionError = () => {
		if (!reportData) return

		Sentry.captureMessage('Transcription error report', {
			level: 'info',
			fingerprint: [
				'transcription-error-report',
				taskId || 'unknown-task',
				String(reportData.start ?? 'unknown-start'),
				String(reportData.end ?? 'unknown-end'),
				getFingerprintText(reportData.selectedText)
			],
			tags: {
				feature: 'transcription-error-report',
				...(taskId ? { task_id: taskId } : {})
			},
			contexts: {
				transcription_error: {
					selected_text: reportData.selectedText,
					page_url: window.location.href,
					start_sec: reportData.start,
					end_sec: reportData.end,
					start_timecode:
						reportData.start !== undefined
							? displayTime(reportData.start)
							: undefined,
					end_timecode:
						reportData.end !== undefined
							? displayTime(reportData.end)
							: undefined,
					user_name: username || undefined,
					user_email: userEmail || undefined
				}
			}
		})

		toast.success('Transcription error report sent')
		setReportData(null)
		window.getSelection()?.removeAllRanges()
	}

	return {
		transcriptionTextRef,
		reportData,
		reportTranscriptionError
	}
}
